/**
 * Diese Komponente bindet dann das jeweilige Modul ein.
 */

import 'regenerator-runtime/runtime'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { makeStyles } from '@mui/styles'
import { Box, Grid, Paper, Theme, FormControlLabel, IconButton, Stack, Typography, CircularProgress, TablePagination } from '@mui/material'

import MediaGalleryComponent
  from '@professocial/professocialFormComponents/FormComponents/MediaGallery/MediaGalleryComponent'
import { useDispatch, useSelector } from 'react-redux'
import {
  ListMediaFilesRequest,
  MediaFileMetadata,
  UploadMediaFileRequest,
} from '@3m5/sm_publish-frontend-api'
import { listMediaFilesInitiated } from '../../../../store/images/images.actions'
import { getMediaData, getMediaFile, getThumbnailForMediaFile, getTotalMediaFilesCount } from '../../../../store/images/images.selector'
import MediaUploadComponent from '@professocial/professocialFormComponents/FormComponents/MediaUploadComponent'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import DeleteIcon from '@mui/icons-material/Delete'
import SwitchComponent from '@professocial/professocialFormComponents/FormComponents/SwitchComponent'
import DatePickerComponent from '@professocial/professocialFormComponents/FormComponents/DatePickerComponent'
import VideoComponent from '@professocial/professocialFormComponents/FormComponents/VideoComponent'
import { CoreRootState } from '../../../../store/coreRoot.reducer'
import { namedRequestInProgress, namedRequestStatus } from '../../../../store/common/common.selectors'
import {
  deleteMediaFileInitiated,
  setMediaFileUsageInitiated, uploadMediaFileInitiated, uploadThumbnailForMediaFileInitiated,
} from '../../../../store/images/images.actions'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { UploadThumbnailForMediaFileRequest } from '@3m5/sm_publish-frontend-api'
import { MediaFileSorting, MediaFileUsageType } from '@3m5/sm_publish-frontend-api'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    marginTop: '10px',
    padding: '25px 30px',
  },
  button: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
    textTransform: 'initial',
    marginRight: '20px',
  },
  upload: {
    margin: theme.spacing(2),
    marginLeft: 0,
  },
  previewPopUp: {
    overflow: 'hidden',
  },
  imageNoBorder: {
    margin: 0,
    minHeight: '300px',
    width: '100%',
    objectFit: 'cover',
  },
  deleteIcon: {
    position: 'absolute',
    right: 10,
    top: 10,
    backgroundColor: 'white',
    borderRadius: '50%',
    zIndex: 10,

    '&:hover': {
      backgroundColor: 'white',
    }
  },
  mediaCache: {
    position: 'fixed',
    top: -9999,
    left: -9999,
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: 'none',
    visibility: 'hidden',
  },
}))

interface Props {
  getMediaFiles: (queryParams: ListMediaFilesRequest) => void
  deleteFile: (id: string) => void
  uploadNewFile: (request: UploadMediaFileRequest) => void
  getMediaFileData: (fileId: string) => void
}

const EditorImagesMediaGallery: React.FC<Props> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const mediaData: MediaFileMetadata[] = useSelector(getMediaData) || []
  const mediaFile: MediaFileMetadata | undefined = useSelector(getMediaFile)
  const withThumbnailMediaFile: MediaFileMetadata | undefined = useSelector(getThumbnailForMediaFile)
  const totalMediaFilesCount: number | undefined = useSelector(getTotalMediaFilesCount)

  const [uploadedMediaFile, setUploadedMediaFile] = useState<UploadMediaFileRequest>()
  const [selectedMedia, setSelectedMedia] = useState<MediaFileMetadata>()
  const [isVideo, setIsVideo] = useState<boolean>(false)
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [infoDialog, setInfoDialog] = useState<boolean>(false)

  // Track all video URLs with their MIME types - Map<URL, MIME-Type>
  const [cachedVideoUrls, setCachedVideoUrls] = useState<Map<string, string>>(new Map())
  // Track all image URLs to ensure they're properly cached
  const [cachedImageUrls, setCachedImageUrls] = useState<Set<string>>(new Set())
  // Blob URL cache for videos to prevent re-downloading
  const [videoBlobCache, setVideoBlobCache] = useState<Map<string, string>>(new Map())

  const [queryParams, setQueryParams] = useState<ListMediaFilesRequest>({
    limit: 50,
    offset: 0,
    sort: MediaFileSorting.UploadedAt,
    sortDesc: true,
    includeUsage: true,
    usageType: MediaFileUsageType.Posting,
    onlyEditorFiles: true
  })
  const [itemsPerPage] = useState<number>(50)
  
  // State for visibility settings
  const [visibilitySettings, setVisibilitySettings] = useState<{
    currentlyInUse: boolean
    validFrom: Date | null
    validUntil: Date | null
  }>({
    currentlyInUse: false,
    validFrom: null,
    validUntil: null
  })

  const selectedMediaFileData = useSelector(getMediaFile)
  const loadingMediaFile: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, uploadMediaFileInitiated.type))
  const loadingThumbnail: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, uploadThumbnailForMediaFileInitiated.type))
  const loadingMediaData: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, listMediaFilesInitiated.type))
  
  // Initialize visibility settings when media is selected
  useEffect(() => {
    if (selectedMediaFileData) {
      setVisibilitySettings({
        currentlyInUse: selectedMediaFileData.currentlyInUse || false,
        validFrom: selectedMediaFileData.validFrom ? new Date(selectedMediaFileData.validFrom) : null,
        validUntil: selectedMediaFileData.validUntil ? new Date(selectedMediaFileData.validUntil) : null
      })
    }
  }, [selectedMediaFileData])
  
  // Handle successful setMediaUsage update
  useEffect(() => {
    if (statusOnSetMediaFile === 'success' && selectedMedia) {
      // Refresh the selected media file data to show updated values
      props.getMediaFileData(selectedMedia.mediaFileId)
      // Refresh the media list to show updated status
      props.getMediaFiles(queryParams)
    }
  }, [selectedMedia, props, queryParams])
  
  const statusOnSetMediaFile: string | undefined = useSelector((state: CoreRootState) => namedRequestStatus(state, setMediaFileUsageInitiated.type))
  const statusOnDeleteMediaFile: string | undefined = useSelector((state: CoreRootState) => namedRequestStatus(state, deleteMediaFileInitiated.type))
  const statusOnUploadMediaFile: string | undefined = useSelector((state: CoreRootState) => namedRequestStatus(state, uploadMediaFileInitiated.type))

  const dispatchUploadThumbnailForMediaFile = useCallback((params: UploadThumbnailForMediaFileRequest) => dispatch(uploadThumbnailForMediaFileInitiated(params)), [dispatch])
  const dispatchSetMediaFileUsage = useCallback((params: any) => dispatch(setMediaFileUsageInitiated(params)), [dispatch])
  
  // Function to save visibility settings
  const handleSaveVisibilitySettings = () => {
    if (selectedMedia) {
      const setMediaUsageParams = {
        mediaFileId: selectedMedia.mediaFileId,
        // Note: usageType is not sent according to API spec
        isPrivate: !visibilitySettings.currentlyInUse, // isPrivate is inverted from currentlyInUse (sichtbar)
        validFrom: visibilitySettings.validFrom || undefined,
        validUntil: visibilitySettings.validUntil || undefined
      }
      dispatchSetMediaFileUsage(setMediaUsageParams)
    }
  }

  const handleSelectedMedia = (item: MediaFileMetadata, thumbnail: string | undefined) => {
    const itemIsVideo = item.mimeType?.includes('video')
    setIsVideo(itemIsVideo)
    setSelectedMedia(item)
  }

  // Preload all videos and images when mediaData changes
  useEffect(() => {
    if (mediaData && mediaData.length > 0) {
      const videoUrlsWithMimeTypes = new Map<string, string>()
      const imageUrls = new Set<string>()

      mediaData.forEach((item) => {
        if (item.directLink && item.mimeType) {
          if (item.mimeType.includes('video')) {
            // Store video URL with its MIME type for proper decoding
            videoUrlsWithMimeTypes.set(item.directLink, item.mimeType)
          } else if (item.mimeType.includes('image')) {
            imageUrls.add(item.directLink)
          }
        }
      })

      setCachedVideoUrls(videoUrlsWithMimeTypes)
      setCachedImageUrls(imageUrls)
    }
  }, [mediaData])

  // Load videos as blobs with correct MIME types to prevent decoding errors
  useEffect(() => {
    if (cachedVideoUrls.size > 0) {
      const loadVideoBlobs = async () => {
        const newBlobCache = new Map<string, string>()

        for (const [videoUrl, mimeType] of Array.from(cachedVideoUrls.entries())) {
          // Skip if already cached
          if (videoBlobCache.has(videoUrl)) {
            newBlobCache.set(videoUrl, videoBlobCache.get(videoUrl)!)
            continue
          }

          try {
            const response = await fetch(videoUrl)
            const arrayBuffer = await response.arrayBuffer()

            // Create blob with explicit MIME type from metadata to ensure proper decoding
            // This fixes the NS_ERROR_DOM_MEDIA_METADATA_ERR error
            const blob = new Blob([arrayBuffer], { type: mimeType })
            const blobUrl = URL.createObjectURL(blob)
            newBlobCache.set(videoUrl, blobUrl)
          } catch (error) {
            console.error(`Failed to load video blob for ${videoUrl}:`, error)
            // Fallback to original URL on error
            newBlobCache.set(videoUrl, videoUrl)
          }
        }

        setVideoBlobCache(newBlobCache)
      }

      loadVideoBlobs()
    }

    // Cleanup blob URLs on unmount
    return () => {
      videoBlobCache.forEach((blobUrl) => {
        if (blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl)
        }
      })
    }
  }, [cachedVideoUrls])

  useEffect(() => {
    props.getMediaFiles(queryParams)
  }, [queryParams])


  useEffect(() => {
    if (uploadedMediaFile && uploadedMediaFile.file.type.includes('video')) {
      setIsVideo(true)
    }
  }, [uploadedMediaFile])

  useEffect(() => {
    selectedMedia && Object.keys(selectedMedia).length > 0 && setInfoDialog(true)
  }, [selectedMedia])

  useEffect(() => {
    if (statusOnUploadMediaFile && statusOnUploadMediaFile === 'success') {
      setUploadedMediaFile(undefined)
      setIsVideo(false)
      // Reload the page after successful upload to show the newest upload
      props.getMediaFiles(queryParams)
    }
    statusOnSetMediaFile && statusOnSetMediaFile === 'success' && props.getMediaFiles(queryParams)
    statusOnDeleteMediaFile && statusOnDeleteMediaFile === 'success' && props.getMediaFiles(queryParams)
  }, [statusOnSetMediaFile, statusOnDeleteMediaFile, statusOnUploadMediaFile])

  useEffect(() => {
    if (withThumbnailMediaFile) {
      setSelectedMedia(withThumbnailMediaFile)
    }
  }, [withThumbnailMediaFile])

  return (
    <>
      <Grid container>
        <Grid xs={12} item>
          <Paper className={classes.paper}>
            <Box>{t('coreImages:mediaGalleryDesc')}</Box>
            <Box className={classes.upload}>
              <MediaUploadComponent
                getMediaData={(params) => setUploadedMediaFile(params)}
                setMediaFile={(file) => {
                  if (file === undefined && uploadedMediaFile) {
                    setUploadedMediaFile(undefined)
                    setIsVideo(false)
                  }
                }}
                loading={loadingMediaFile}
                isUploaded={!mediaFile}
                isVideo={isVideo}
                allowImageUpload
                allowVideoUpload
                onlyUploadButton
                cropBoxResizable
              />
              {uploadedMediaFile && <Box sx={{ pt: 3 }}>
                <PreloaderButton
                  loading={loadingMediaFile}
                  disableButton={loadingMediaFile}
                  position="left"
                  onClick={() => props.uploadNewFile(uploadedMediaFile)}
                  text={t('commonImage:uploadNewFile')}
                />
              </Box>}
            </Box>
          </Paper>
        </Grid>
        <Grid xs={12} item>
          <Paper className={classes.paper}>
            {loadingMediaData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <MediaGalleryComponent
                  mediaData={mediaData}
                  setSelected={handleSelectedMedia}
                  selected={selectedMedia}
                  doNotShowAsSelected
                  itemsProPage={itemsPerPage}
                  totalCount={totalMediaFilesCount}
                  onPageChange={(newPage) => {
                    setQueryParams({
                      ...queryParams,
                      offset: (newPage - 1) * itemsPerPage
                    })
                  }}
                />
              </>
            )}
          </Paper>
        </Grid>

        {/* Hidden image elements for caching - kept in DOM but invisible */}
        <Box className={classes.mediaCache}>
          {Array.from(cachedImageUrls).map((imageUrl) => (
            <img
              key={imageUrl}
              src={imageUrl}
              style={{ width: '1px', height: '1px' }}
              alt=""
            />
          ))}
        </Box>

        <Box component="div" sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', mt: '10px' }}>
          <DialogComponent
            open={infoDialog}
            maxWidth="md"
            content={
              <Grid container item xs={12} className={classes.previewPopUp}>
                <Grid item xs={12} md={7} sx={{ position: 'relative', display: 'flex' }}>
                  <IconButton size="small" className={classes.deleteIcon} onClick={() => setDeleteDialog(true)}>
                    <DeleteIcon color="primary"/>
                  </IconButton>

                  {selectedMedia?.directLink && selectedMedia?.mimeType?.includes('image') &&
                    <ImageComponent
                      fallback=""
                      src={selectedMedia?.directLink}
                      className={classes.imageNoBorder}/>}

                  {selectedMedia?.mimeType?.includes('video') &&
                    <VideoComponent
                      fallbackThumb={selectedMedia?.thumbnailMediaFileUrl}
                      src={videoBlobCache.get(selectedMedia?.directLink || '') || selectedMedia?.directLink}
                      className={classes.imageNoBorder}
                      height='300px'
                      width='525px'
                      withThumbnail={!!selectedMedia?.thumbnailMediaFileUrl}
                      controls
                    />}
                </Grid>
                <Grid item xs={12} md={5} sx={{ p: 2.5 }}>
                  <Box sx={{ px: 1.5, py: 2.5, width: '100%' }}>
                    <FormControlLabel
                      label={t('commonImage:visible') as string}
                      control={
                        <SwitchComponent 
                          checked={visibilitySettings.currentlyInUse}
                          onChange={(event, checked) => setVisibilitySettings(prev => ({ ...prev, currentlyInUse: checked }))}
                        />
                      }
                    />
                  </Box>
                  <Stack direction="row" spacing={2}>
                    <Box>
                      <DatePickerComponent
                        value={visibilitySettings.validFrom || undefined}
                        size="small"
                        label={t('commonImage:visibleFrom')}
                        inputLabelShrink
                        onChangeDate={(date: Date | null | undefined) => setVisibilitySettings(prev => ({ ...prev, validFrom: date || null }))}
                      />
                    </Box>
                    <Box>
                      <DatePickerComponent
                        value={visibilitySettings.validUntil || undefined}
                        size="small"
                        label={t('commonImage:visibleUntil')}
                        inputLabelShrink
                        onChangeDate={(date: Date | null | undefined) => setVisibilitySettings(prev => ({ ...prev, validUntil: date || null }))}
                      />
                    </Box>
                  </Stack>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <PreloaderButton
                      text={t('coreCommon:save')}
                      position="left"
                      variant="contained"
                      color="primary"
                      onClick={handleSaveVisibilitySettings}
                      loading={statusOnSetMediaFile === 'pending'}
                      disabled={!selectedMedia}
                    />
                  </Box>
                  {!selectedMedia?.thumbnailMediaFileUrl && isVideo && (
                    <>
                      <Typography sx={{ mt: 3, mb: 1 }}>Thumbnail hochladen:</Typography>
                      <MediaUploadComponent
                        loading={loadingThumbnail}
                        isUploaded={!mediaFile}
                        isVideo={false}
                        getMediaData={({ file }) => {
                          if (selectedMedia) {
                            dispatchUploadThumbnailForMediaFile({ mediaFileId: selectedMedia.mediaFileId, file })
                          }
                        }}
                        aspectRatio={1}
                        withButtonPreloader
                        onlyUploadButton
                        cropBoxResizable
                        modalCrop
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            }
            onClose={() => {
              setInfoDialog(false)
            }}
            autoClose={false}
            closeIcon
            noBorder
          />
          <DialogComponent
            open={deleteDialog}
            type="warning"
            title={t('coreImages:deleteFileWarning')}
            requestActions
            confirmLabel={t('coreImages:deleteFile')}
            cancelLabel={t('coreCommon:backToPAge')}
            autoClose={false}
            closeIcon={false}
            onClose={() => setDeleteDialog(false)}
            onConfirm={() => {
              selectedMedia && props.deleteFile(selectedMedia.mediaFileId)
              setDeleteDialog(false)
              setInfoDialog(false)
              props.getMediaFiles(queryParams)
            }}
            onCancel={() => setDeleteDialog(false)}
          />
        </Box>
      </Grid>
    </>
  )
}
export default EditorImagesMediaGallery
